import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Loader2, Eye, FileDown, Plus, Trash2, Sun, Moon, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Combobox } from '../components/ui/combobox';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from '../components/ui/alert-dialog';
import Logo from '../components/Logo';
import ApplicationTimeline from '../components/ApplicationTimeline';
import { vendorFetch, VendorAuthError, VENDOR_BASE_PATH, VENDOR_TOKEN_KEY } from '../lib/vendorApi';
import { exportToExcel, slugify } from '../lib/exportExcel';
import { useTheme } from '../hooks/useTheme';
import { COUNTRIES } from '../lib/countries';
import { COUNTRY_CODES } from '../lib/countryCodes';

const EMPTY_ADD_FORM = {
  full_name: '',
  passport_number: '',
  nationality: '',
  email: '',
  phone_country_code: '',
  phone_number: '',
  address: '',
};
const EMPTY_TALK_FORM = {
  candidate_full_name: '',
  candidate_file_number: '',
  candidate_passport_number: '',
  subject: '',
  message: '',
};

const VendorDashboard = () => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [activeTab, setActiveTab] = useState('applications'); // 'applications' | 'queries'
  const [profile, setProfile] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  const [viewingApplication, setViewingApplication] = useState(null);
  const [viewingLoading, setViewingLoading] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [addValues, setAddValues] = useState(EMPTY_ADD_FORM);
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);

  const [queries, setQueries] = useState([]);
  const [queriesLoading, setQueriesLoading] = useState(true);
  const [talkOpen, setTalkOpen] = useState(false);
  const [talkValues, setTalkValues] = useState(EMPTY_TALK_FORM);
  const [talkSaving, setTalkSaving] = useState(false);
  const [viewingQuery, setViewingQuery] = useState(null);
  const [viewingQueryLoading, setViewingQueryLoading] = useState(false);

  const goToLogin = useCallback(() => {
    navigate(`/${VENDOR_BASE_PATH}/login`);
  }, [navigate]);

  const loadProfile = useCallback(async () => {
    try {
      const res = await vendorFetch('/me');
      if (res.ok) setProfile(await res.json());
    } catch (err) {
      if (err instanceof VendorAuthError) goToLogin();
    }
  }, [goToLogin]);

  const loadApplications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await vendorFetch('/applications');
      if (!res.ok) {
        toast.error('Failed to load applications.');
        return;
      }
      setApplications(await res.json());
    } catch (err) {
      if (err instanceof VendorAuthError) goToLogin();
      else toast.error('Failed to load applications.');
    } finally {
      setLoading(false);
    }
  }, [goToLogin]);

  const loadQueries = useCallback(async () => {
    setQueriesLoading(true);
    try {
      const res = await vendorFetch('/queries');
      if (!res.ok) {
        toast.error('Failed to load queries.');
        return;
      }
      setQueries(await res.json());
    } catch (err) {
      if (err instanceof VendorAuthError) goToLogin();
      else toast.error('Failed to load queries.');
    } finally {
      setQueriesLoading(false);
    }
  }, [goToLogin]);

  useEffect(() => {
    if (!sessionStorage.getItem(VENDOR_TOKEN_KEY)) {
      goToLogin();
      return;
    }
    loadProfile();
    loadApplications();
    loadQueries();
  }, [goToLogin, loadProfile, loadApplications, loadQueries]);

  const handleExport = () => {
    const filename = `${slugify(profile?.name || 'vendor')}-applications`;
    exportToExcel(
      filename,
      applications.map((a) => ({
        'File No.': a.file_number || 'Pending',
        Name: a.full_name,
        'Passport No.': a.passport_number,
        Nationality: a.nationality,
        'Current Stage': a.current_stage_label || 'Not started',
      })),
    );
  };

  const handleLogout = () => {
    sessionStorage.removeItem(VENDOR_TOKEN_KEY);
    goToLogin();
  };

  const openApplication = async (applicationId) => {
    setViewingLoading(true);
    try {
      const res = await vendorFetch(`/applications/${applicationId}`);
      if (!res.ok) {
        toast.error('Failed to load application details.');
        return;
      }
      setViewingApplication(await res.json());
    } catch (err) {
      if (err instanceof VendorAuthError) goToLogin();
      else toast.error('Failed to load application details.');
    } finally {
      setViewingLoading(false);
    }
  };

  const openQuery = async (queryId) => {
    setViewingQueryLoading(true);
    try {
      const res = await vendorFetch(`/queries/${queryId}`);
      if (!res.ok) {
        toast.error('Failed to load query details.');
        return;
      }
      setViewingQuery(await res.json());
    } catch (err) {
      if (err instanceof VendorAuthError) goToLogin();
      else toast.error('Failed to load query details.');
    } finally {
      setViewingQueryLoading(false);
    }
  };

  // Only candidates with an assigned file number can be referenced in a
  // query - the backend match requires an exact file_number + passport
  // pair, and file_number stays null until an admin processes the
  // candidate (see the "Pending" applications table cell).
  const assignableCandidates = applications.filter((application) => application.file_number);
  const assignedFullNames = assignableCandidates.map((a) => a.full_name);
  const assignedFileNumbers = assignableCandidates.map((a) => a.file_number);
  const assignedPassportNumbers = assignableCandidates.map((a) => a.passport_number);

  // Selecting any one of Name / File Number / Passport No. looks up the
  // same candidate and fills in the other two, so the vendor only ever has
  // to pick one identifying value.
  const applyCandidateMatch = (matched, fallback) => ({
    candidate_full_name: matched ? matched.full_name : fallback.candidate_full_name,
    candidate_file_number: matched ? matched.file_number : fallback.candidate_file_number,
    candidate_passport_number: matched ? matched.passport_number : fallback.candidate_passport_number,
  });

  const handleTalkNameChange = (fullName) => {
    const matched = assignableCandidates.find((a) => a.full_name === fullName);
    setTalkValues((prev) => ({
      ...prev,
      ...applyCandidateMatch(matched, prev),
      candidate_full_name: fullName,
    }));
  };

  const handleTalkFileNumberChange = (fileNumber) => {
    const matched = assignableCandidates.find((a) => a.file_number === fileNumber);
    setTalkValues((prev) => ({
      ...prev,
      ...applyCandidateMatch(matched, prev),
      candidate_file_number: fileNumber,
    }));
  };

  const handleTalkPassportChange = (passportNumber) => {
    const matched = assignableCandidates.find((a) => a.passport_number === passportNumber);
    setTalkValues((prev) => ({
      ...prev,
      ...applyCandidateMatch(matched, prev),
      candidate_passport_number: passportNumber,
    }));
  };

  const handleTalkSubmit = async (e) => {
    e.preventDefault();
    setTalkSaving(true);
    try {
      const res = await vendorFetch('/queries', {
        method: 'POST',
        body: JSON.stringify({
          candidate_file_number: talkValues.candidate_file_number,
          candidate_passport_number: talkValues.candidate_passport_number,
          subject: talkValues.subject,
          message: talkValues.message,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        toast.error(body?.detail || 'Failed to submit your query.');
        return;
      }
      toast.success('Your query has been sent to Zeevogo.');
      setTalkOpen(false);
      setTalkValues(EMPTY_TALK_FORM);
      loadQueries();
    } catch (err) {
      if (err instanceof VendorAuthError) goToLogin();
      else toast.error('Failed to submit your query.');
    } finally {
      setTalkSaving(false);
    }
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await vendorFetch('/applications', {
        method: 'POST',
        body: JSON.stringify(addValues),
      });
      if (!res.ok) {
        toast.error('Failed to add candidate.');
        return;
      }
      toast.success('Candidate added.');
      setAddOpen(false);
      setAddValues(EMPTY_ADD_FORM);
      loadApplications();
    } catch (err) {
      if (err instanceof VendorAuthError) goToLogin();
      else toast.error('Failed to add candidate.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const res = await vendorFetch(`/applications/${deleteTarget.id}`, { method: 'DELETE' });
      if (!res.ok && res.status !== 204) {
        toast.error('Failed to delete candidate.');
        return;
      }
      toast.success('Candidate deleted.');
      setDeleteTarget(null);
      loadApplications();
    } catch (err) {
      if (err instanceof VendorAuthError) goToLogin();
      else toast.error('Failed to delete candidate.');
    }
  };

  return (
    <div className="min-h-screen bg-zg-surface">
      <div className="border-b border-zg-border bg-zg-card">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo markSize={30} wordmarkSize={22} />
            <span className="hidden sm:block h-5 w-px" style={{ background: 'var(--zg-border2)' }} />
            <span className="hidden sm:inline text-sm font-semibold" style={{ color: 'var(--zg-muted)' }}>
              Zeevogo Vendor Admin Portal
            </span>
          </div>
          <Button variant="outline" size="icon" onClick={toggleTheme} title="Toggle theme">
            {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      <div className="py-10 px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setActiveTab('applications')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'applications' ? 'bg-zg-espresso text-white' : 'text-zg-muted hover:bg-zg-surface2'
                }`}
              >
                My Applications
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('queries')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'queries' ? 'bg-zg-espresso text-white' : 'text-zg-muted hover:bg-zg-surface2'
                }`}
              >
                My Queries
              </button>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>

          {activeTab === 'applications' && (
            <>
              <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                <div>
                  {profile && <p className="text-sm text-zg-muted mb-1">Hi, {profile.name}</p>}
                  <h1 className="text-2xl font-bold text-zg-ink">My Applications</h1>
                </div>
                <div className="flex items-center gap-3">
                  <Button variant="outline" onClick={() => setTalkOpen(true)}>
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Talk to Employer
                  </Button>
                  <Button onClick={() => setAddOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Candidate
                  </Button>
                  <Button variant="outline" onClick={handleExport} disabled={applications.length === 0}>
                    <FileDown className="h-4 w-4 mr-2" />
                    Export to Excel
                  </Button>
                </div>
              </div>

              <Card className="border-zg-border rounded-none">
                <CardContent className="p-0">
                  {loading ? (
                    <div className="flex items-center justify-center py-16">
                      <Loader2 className="h-6 w-6 text-zg-muted animate-spin" />
                    </div>
                  ) : applications.length === 0 ? (
                    <div className="py-16 text-center text-sm text-zg-muted">
                      No applications have been assigned to you yet.
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>File No.</TableHead>
                          <TableHead>Name</TableHead>
                          <TableHead>Passport No.</TableHead>
                          <TableHead>Nationality</TableHead>
                          <TableHead>Current Stage</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {applications.map((application) => (
                          <TableRow
                            key={application.id}
                            className="cursor-pointer"
                            onClick={() => openApplication(application.id)}
                          >
                            <TableCell className="font-mono">{application.file_number || 'Pending'}</TableCell>
                            <TableCell>{application.full_name}</TableCell>
                            <TableCell className="font-mono">{application.passport_number}</TableCell>
                            <TableCell>{application.nationality}</TableCell>
                            <TableCell>{application.current_stage_label || 'Not started'}</TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openApplication(application.id);
                                }}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              {!application.file_number && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDeleteTarget(application);
                                  }}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </>
          )}

          {activeTab === 'queries' && (
            <>
              <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                <h1 className="text-2xl font-bold text-zg-ink">My Queries</h1>
                <Button variant="outline" onClick={() => setTalkOpen(true)}>
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Talk to Employer
                </Button>
              </div>

              <Card className="border-zg-border rounded-none">
                <CardContent className="p-0">
                  {queriesLoading ? (
                    <div className="flex items-center justify-center py-16">
                      <Loader2 className="h-6 w-6 text-zg-muted animate-spin" />
                    </div>
                  ) : queries.length === 0 ? (
                    <div className="py-16 text-center text-sm text-zg-muted">
                      You haven't raised any queries yet.
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12">Sr No.</TableHead>
                          <TableHead>Vendor Name</TableHead>
                          <TableHead>Subject</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {queries.map((query, index) => (
                          <TableRow key={query.id} className="cursor-pointer" onClick={() => openQuery(query.id)}>
                            <TableCell>{index + 1}</TableCell>
                            <TableCell>{query.vendor_name}</TableCell>
                            <TableCell>{query.subject}</TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openQuery(query.id);
                                }}
                              >
                                <Eye className="h-3.5 w-3.5 mr-1" />
                                View Query
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>

      <Dialog open={viewingApplication !== null} onOpenChange={(open) => !open && setViewingApplication(null)}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{viewingApplication?.full_name}</DialogTitle>
          </DialogHeader>
          {viewingLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 text-zg-muted animate-spin" />
            </div>
          ) : (
            viewingApplication && (
              <div>
                <div className="grid grid-cols-2 gap-4 pb-6 mb-6 border-b border-zg-border text-sm">
                  <div>
                    <p className="text-zg-muted">File No.</p>
                    <p className="font-mono text-zg-ink">{viewingApplication.file_number || 'Pending'}</p>
                  </div>
                  <div>
                    <p className="text-zg-muted">Passport No.</p>
                    <p className="font-mono text-zg-ink">{viewingApplication.passport_number}</p>
                  </div>
                  <div>
                    <p className="text-zg-muted">Nationality</p>
                    <p className="text-zg-ink">{viewingApplication.nationality}</p>
                  </div>
                </div>
                {viewingApplication.notes && (
                  <p className="text-sm text-zg-ink mb-6 rounded-lg bg-zg-surface2 p-3">
                    {viewingApplication.notes}
                  </p>
                )}
                <ApplicationTimeline stages={viewingApplication.stages} />
              </div>
            )
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Candidate</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Full Name</Label>
              <Input
                value={addValues.full_name}
                onChange={(e) => setAddValues((prev) => ({ ...prev, full_name: e.target.value }))}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label>Passport Number</Label>
              <Input
                value={addValues.passport_number}
                onChange={(e) => setAddValues((prev) => ({ ...prev, passport_number: e.target.value }))}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label>Nationality</Label>
              <Combobox
                value={addValues.nationality}
                onChange={(value) => setAddValues((prev) => ({ ...prev, nationality: value }))}
                options={COUNTRIES}
                placeholder="Select country..."
                emptyText="No matching country."
              />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input
                type="email"
                value={addValues.email}
                onChange={(e) => setAddValues((prev) => ({ ...prev, email: e.target.value }))}
                placeholder="candidate@example.com"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Phone</Label>
              <div className="flex gap-2">
                <Select
                  value={addValues.phone_country_code}
                  onValueChange={(value) => setAddValues((prev) => ({ ...prev, phone_country_code: value }))}
                >
                  <SelectTrigger className="w-36 shrink-0">
                    <SelectValue placeholder="Code" />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRY_CODES.map((code) => (
                      <SelectItem key={code.label} value={code.value}>
                        {code.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  value={addValues.phone_number}
                  onChange={(e) => setAddValues((prev) => ({ ...prev, phone_number: e.target.value }))}
                  placeholder="Phone number"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Address</Label>
              <Textarea
                rows={2}
                value={addValues.address}
                onChange={(e) => setAddValues((prev) => ({ ...prev, address: e.target.value }))}
              />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={talkOpen} onOpenChange={setTalkOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Talk to Employer</DialogTitle>
          </DialogHeader>
          {assignedFileNumbers.length === 0 ? (
            <p className="text-sm text-zg-muted py-6">
              None of your candidates have an assigned File Number yet, so there's nothing to raise a
              query about. Check back once a candidate has been processed.
            </p>
          ) : (
            <form onSubmit={handleTalkSubmit} className="space-y-4">
              <p className="text-sm text-zg-muted">
                Select the candidate by Name, File Number, or Passport No. - picking any one fills in
                the other two.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <Label>Candidate Name</Label>
                  <Combobox
                    value={talkValues.candidate_full_name}
                    onChange={handleTalkNameChange}
                    options={assignedFullNames}
                    placeholder="Select name..."
                    emptyText="No matching candidate."
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Candidate File Number</Label>
                  <Combobox
                    value={talkValues.candidate_file_number}
                    onChange={handleTalkFileNumberChange}
                    options={assignedFileNumbers}
                    placeholder="Select file number..."
                    emptyText="No matching file number."
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Candidate Passport ID</Label>
                  <Combobox
                    value={talkValues.candidate_passport_number}
                    onChange={handleTalkPassportChange}
                    options={assignedPassportNumbers}
                    placeholder="Select passport no..."
                    emptyText="No matching passport."
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Subject Line</Label>
                <Input
                  value={talkValues.subject}
                  onChange={(e) => setTalkValues((prev) => ({ ...prev, subject: e.target.value }))}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label>Message</Label>
                <Textarea
                  value={talkValues.message}
                  onChange={(e) => setTalkValues((prev) => ({ ...prev, message: e.target.value }))}
                  rows={6}
                  required
                />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={talkSaving || !talkValues.candidate_file_number}>
                  {talkSaving ? 'Sending...' : 'Send Query'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={viewingQuery !== null} onOpenChange={(open) => !open && setViewingQuery(null)}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{viewingQuery?.subject}</DialogTitle>
          </DialogHeader>
          {viewingQueryLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 text-zg-muted animate-spin" />
            </div>
          ) : (
            viewingQuery && (
              <div>
                <div className="grid grid-cols-2 gap-4 pb-6 mb-6 border-b border-zg-border text-sm">
                  <div>
                    <p className="text-zg-muted">Candidate Name</p>
                    <p className="text-zg-ink">{viewingQuery.candidate_full_name}</p>
                  </div>
                  <div>
                    <p className="text-zg-muted">Candidate File No.</p>
                    <p className="font-mono text-zg-ink">{viewingQuery.candidate_file_number}</p>
                  </div>
                  <div>
                    <p className="text-zg-muted">Candidate Passport ID</p>
                    <p className="font-mono text-zg-ink">{viewingQuery.candidate_passport_number}</p>
                  </div>
                  <div>
                    <p className="text-zg-muted">Submitted</p>
                    <p className="text-zg-ink">{new Date(viewingQuery.created_at).toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-zg-muted">Status</p>
                    <p className="text-zg-ink">{viewingQuery.resolved ? 'Resolved' : 'Awaiting response'}</p>
                  </div>
                </div>
                <div className="mb-6">
                  <p className="text-zg-muted text-sm mb-1">Your Message</p>
                  <p className="text-sm text-zg-ink rounded-lg bg-zg-surface2 p-3">{viewingQuery.message}</p>
                </div>
                <div>
                  <p className="text-zg-muted text-sm mb-1">Admin's Response</p>
                  <p className="text-sm text-zg-ink rounded-lg bg-zg-surface2 p-3">
                    {viewingQuery.admin_response || 'Awaiting response from Zeevogo.'}
                  </p>
                </div>
              </div>
            )
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this candidate?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove {deleteTarget?.full_name} from your applications. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default VendorDashboard;
